import { useEffect, useState, type FormEvent } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { api } from "../../api";
import { useSession } from "../../shell/session";
import { aucklandLocalToUtc, utcToAucklandInput } from "../../../shared/time";
import type { Group, GroupDetail, Person } from "../../../shared/types";
import { Badge, Dialog, Empty, ErrorNote } from "../../ui";
import "../people/community.css";
import { canCoordinate, errorText, readMarks, safeWhen, usePageTitle, writeMarks } from "../people/support";

const KINDS = ["community", "home", "music", "hospitality", "youth"];

interface GroupForm {
  name: string;
  kind: string;
  meetingTime: string;
  meetingPlace: string;
  description: string;
  leaderPersonId: string;
}

function blankGroup(): GroupForm {
  return { name: "", kind: "community", meetingTime: "", meetingPlace: "", description: "", leaderPersonId: "" };
}

function formFrom(group: GroupDetail): GroupForm {
  return {
    name: group.name,
    kind: group.kind,
    meetingTime: group.meetingTime ?? "",
    meetingPlace: group.meetingPlace ?? "",
    description: group.description,
    leaderPersonId: group.leaderPersonId ?? "",
  };
}

function groupPayload(form: GroupForm) {
  return {
    name: form.name.trim(),
    kind: form.kind.trim() || "community",
    meetingTime: form.meetingTime.trim() || null,
    meetingPlace: form.meetingPlace.trim() || null,
    description: form.description,
    leaderPersonId: form.leaderPersonId || null,
  };
}

function attendanceWord(value: boolean | undefined): string {
  if (value === true) return "Present";
  if (value === false) return "Absent";
  return "Not marked in this browser";
}

export function GroupsPage() {
  const { user } = useSession();
  const navigate = useNavigate();
  const canWrite = canCoordinate(user?.role);
  const [groups, setGroups] = useState<Group[]>([]);
  const [people, setPeople] = useState<Person[]>([]);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState<GroupForm>(blankGroup);
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  usePageTitle("Groups");

  useEffect(() => {
    let live = true;
    api<Group[]>("/api/v1/groups")
      .then((rows) => { if (live) setGroups(rows); })
      .catch((caught: unknown) => { if (live) setError(errorText(caught)); })
      .finally(() => { if (live) setLoading(false); });
    return () => { live = false; };
  }, []);

  useEffect(() => {
    if (!creating) return;
    void api<Person[]>("/api/v1/people?limit=100").then(setPeople).catch(() => setPeople([]));
  }, [creating]);

  async function createGroup(event: FormEvent) {
    event.preventDefault();
    if (!form.name.trim()) {
      setError("Name the group.");
      return;
    }
    setSaving(true);
    setError("");
    try {
      const created = await api<GroupDetail>("/api/v1/groups", { method: "POST", body: JSON.stringify(groupPayload(form)) });
      if (!created?.id) throw new Error("The group was not returned.");
      navigate(`/groups/${created.id}`);
    } catch (caught) {
      setError(errorText(caught));
    } finally {
      setSaving(false);
    }
  }

  return (
    <section className="page">
      <header className="page-head">
        <div>
          <p className="kicker">Community</p>
          <h1 className="page-title">Groups</h1>
          <p className="lede">Membership, a meeting time, and who was in the room.</p>
        </div>
        {canWrite && <button className="btn primary" type="button" onClick={() => { setForm(blankGroup()); setCreating(true); }}>New group</button>}
      </header>
      {!canWrite && <p className="mbc-limit">This login can view groups. Creating and editing needs a coordinator, leader, or owner.</p>}
      {error && <ErrorNote>{error}</ErrorNote>}
      {loading && <p className="muted">Loading groups…</p>}
      {!loading && groups.length === 0 && <Empty title="No groups yet" body="A coordinator can add the first one." />}
      <div className="mbc-groups">
        {groups.map((group) => (
          <Link className="mbc-group" key={group.id} to={`/groups/${group.id}`}>
            <p className="kicker">{group.kind}</p>
            <strong style={{ fontFamily: "var(--display)", fontSize: 28, letterSpacing: "-0.04em" }}>{group.name}</strong>
            <span>{group.meetingTime ?? "Meeting time not set"} · {group.meetingPlace ?? "Place not set"}</span>
            <span className="muted">{group.memberCount} {group.memberCount === 1 ? "member" : "members"} · {group.leaderName ?? "No leader"}</span>
            {group.description && <p className="mbc-clamp">{group.description}</p>}
          </Link>
        ))}
      </div>
      <p className="faint">Deleting a group is not available.</p>
      {creating && (
        <Dialog title="New group" onClose={() => setCreating(false)}>
          <form className="form-grid" onSubmit={(event) => void createGroup(event)}>
            <GroupFields form={form} people={people} onChange={setForm} />
            <button className="btn primary" type="submit" disabled={saving}>{saving ? "Saving…" : "Create group"}</button>
          </form>
        </Dialog>
      )}
    </section>
  );
}

function GroupFields({ form, people, onChange }: { form: GroupForm; people: Person[]; onChange: (next: GroupForm) => void }) {
  const kinds = KINDS.includes(form.kind) ? KINDS : [form.kind, ...KINDS];
  return (
    <>
      <div className="field"><label htmlFor="group-name">Name</label><input id="group-name" value={form.name} onChange={(event) => onChange({ ...form, name: event.target.value })} maxLength={140} required /></div>
      <div className="grid-2">
        <div className="field">
          <label htmlFor="group-kind">Kind</label>
          <select id="group-kind" value={form.kind} onChange={(event) => onChange({ ...form, kind: event.target.value })}>
            {kinds.map((kind) => <option key={kind} value={kind}>{kind}</option>)}
          </select>
        </div>
        <div className="field">
          <label htmlFor="group-leader">Leader</label>
          <select id="group-leader" value={form.leaderPersonId} onChange={(event) => onChange({ ...form, leaderPersonId: event.target.value })}>
            <option value="">No leader</option>
            {people.map((person) => <option key={person.id} value={person.id}>{person.name}</option>)}
          </select>
        </div>
      </div>
      <div className="grid-2">
        <div className="field"><label htmlFor="group-time">Usual time</label><input id="group-time" value={form.meetingTime} onChange={(event) => onChange({ ...form, meetingTime: event.target.value })} maxLength={80} placeholder="Thursday 7:00 pm" /></div>
        <div className="field"><label htmlFor="group-place">Usual place</label><input id="group-place" value={form.meetingPlace} onChange={(event) => onChange({ ...form, meetingPlace: event.target.value })} maxLength={140} /></div>
      </div>
      <div className="field"><label htmlFor="group-about">Description</label><textarea id="group-about" className="mbc-short" value={form.description} onChange={(event) => onChange({ ...form, description: event.target.value })} maxLength={4000} /></div>
      {people.length >= 100 && <p className="faint">The leader list is the first 100 people.</p>}
    </>
  );
}

export function GroupPage() {
  const { groupId = "" } = useParams();
  const { user } = useSession();
  const canWrite = canCoordinate(user?.role);
  const [group, setGroup] = useState<GroupDetail | null>(null);
  const [people, setPeople] = useState<Person[]>([]);
  const [form, setForm] = useState<GroupForm>(blankGroup);
  const [meetingId, setMeetingId] = useState<string | null>(null);
  const [marks, setMarks] = useState<Record<string, boolean>>({});
  const [memberId, setMemberId] = useState("");
  const [memberRole, setMemberRole] = useState("member");
  const [memberQuery, setMemberQuery] = useState("");
  const [confirmRemove, setConfirmRemove] = useState<string | null>(null);
  const [when, setWhen] = useState(() => utcToAucklandInput(new Date(Date.now() + 86_400_000).toISOString()));
  const [place, setPlace] = useState("");
  const [meetingNotes, setMeetingNotes] = useState("");
  const [pendingId, setPendingId] = useState<string | null>(null);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  usePageTitle(group?.name ?? "Group");

  useEffect(() => {
    let live = true;
    setLoading(true);
    Promise.all([
      api<GroupDetail>(`/api/v1/groups/${groupId}`),
      api<Person[]>("/api/v1/people?limit=100"),
    ]).then(([next, roster]) => {
      if (!live) return;
      setGroup(next);
      setForm(formFrom(next));
      setPeople(roster);
      setError("");
    }).catch((caught: unknown) => { if (live) setError(errorText(caught)); })
      .finally(() => { if (live) setLoading(false); });
    return () => { live = false; };
  }, [groupId]);

  useEffect(() => {
    if (!group) return;
    setMeetingId((current) => (current && group.meetings.some((meeting) => meeting.id === current) ? current : group.meetings[0]?.id ?? null));
  }, [group]);

  useEffect(() => {
    if (meetingId) setMarks(readMarks(meetingId));
  }, [meetingId]);

  const meeting = group?.meetings.find((item) => item.id === meetingId) ?? null;
  const needle = memberQuery.trim().toLowerCase();
  const roster = people.filter((person) => !needle || person.name.toLowerCase().includes(needle) || person.instruments.some((item) => item.toLowerCase().includes(needle)));

  async function saveGroup(event: FormEvent) {
    event.preventDefault();
    if (!group) return;
    if (!form.name.trim()) {
      setError("Name the group.");
      return;
    }
    setSaving(true);
    setError("");
    try {
      const saved = await api<GroupDetail>(`/api/v1/groups/${group.id}`, { method: "PATCH", body: JSON.stringify(groupPayload(form)) });
      if (!saved) throw new Error("The group did not save.");
      setGroup(saved);
      setForm(formFrom(saved));
      setNotice("Group saved.");
    } catch (caught) {
      setError(errorText(caught));
    } finally {
      setSaving(false);
    }
  }

  async function addMember(event: FormEvent) {
    event.preventDefault();
    if (!group || !memberId) {
      setError("Choose a person.");
      return;
    }
    if (memberRole.trim().length > 40) {
      setError("Role needs to be 40 characters or fewer.");
      return;
    }
    setError("");
    try {
      const next = await api<GroupDetail>(`/api/v1/groups/${group.id}/members`, {
        method: "POST",
        body: JSON.stringify({ personId: memberId, role: memberRole.trim() || "member" }),
      });
      setGroup(next);
      setNotice("Membership saved.");
    } catch (caught) {
      setError(errorText(caught));
    }
  }

  async function removeMember(personId: string) {
    if (!group) return;
    setError("");
    try {
      setGroup(await api<GroupDetail>(`/api/v1/groups/${group.id}/members/${personId}`, { method: "DELETE" }));
      setConfirmRemove(null);
      setNotice("Member removed.");
    } catch (caught) {
      setError(errorText(caught));
    }
  }

  async function addMeeting(event: FormEvent) {
    event.preventDefault();
    if (!group) return;
    setError("");
    try {
      const startsAt = aucklandLocalToUtc(when);
      const previous = new Set(group.meetings.map((item) => item.id));
      const next = await api<GroupDetail>(`/api/v1/groups/${group.id}/meetings`, {
        method: "POST",
        body: JSON.stringify({ startsAt, place: place.trim(), notes: meetingNotes.trim() }),
      });
      setGroup(next);
      const created = next.meetings.find((item) => !previous.has(item.id));
      if (created) setMeetingId(created.id);
      setMeetingNotes("");
      setNotice("Meeting saved.");
    } catch (caught) {
      setError(errorText(caught));
    }
  }

  async function mark(personId: string, present: boolean) {
    if (!meeting) return;
    setPendingId(personId);
    setError("");
    try {
      const next = await api<GroupDetail>(`/api/v1/groups/meetings/${meeting.id}/attendance`, {
        method: "POST",
        body: JSON.stringify({ personId, present }),
      });
      setGroup(next);
      setMarks((current) => {
        const updated = { ...current, [personId]: present };
        writeMarks(meeting.id, updated);
        return updated;
      });
    } catch (caught) {
      setError(errorText(caught));
    } finally {
      setPendingId(null);
    }
  }

  if (loading) return <section className="page">Loading the group…</section>;
  if (!group) return <section className="page"><ErrorNote>{error || "Group not found"}</ErrorNote><Link to="/groups">Back to groups</Link></section>;

  return (
    <section className="page">
      <header className="page-head">
        <div>
          <Link className="kicker" to="/groups">Groups</Link>
          <h1 className="page-title">{group.name}</h1>
          <p className="lede">{group.meetingTime ?? "Time not set"} · {group.meetingPlace ?? "Place not set"} · {group.leaderName ?? "No leader"}</p>
        </div>
        <Badge tone="violet">{group.kind} · {group.memberCount}</Badge>
      </header>
      {error && <ErrorNote>{error}</ErrorNote>}
      {notice && <p className="mbc-status" role="status">{notice}</p>}
      {canWrite ? (
        <form className="panel panel-pad form-grid" onSubmit={(event) => void saveGroup(event)}>
          <GroupFields form={form} people={leaderOptions(people, group)} onChange={setForm} />
          <button className="btn primary" type="submit" disabled={saving}>{saving ? "Saving…" : "Save group"}</button>
        </form>
      ) : (
        <p className="mbc-limit">Coordinators can edit this group, its membership, and attendance. Deleting a group is not available.</p>
      )}
      {group.description && !canWrite && <p className="muted">{group.description}</p>}
      <div className="grid-2">
        <article className="panel panel-pad">
          <h2>Members</h2>
          {group.members.length === 0 && <p className="muted">No members yet.</p>}
          {group.members.map((member) => (
            <div className="mbc-member" key={member.personId}>
              <Link to={`/people/${member.personId}`}><strong>{member.name}</strong><div className="faint">{member.instruments.join(", ") || "No instrument"}</div></Link>
              <Badge>{member.role}</Badge>
              {canWrite && confirmRemove !== member.personId && <button className="btn small danger" type="button" onClick={() => setConfirmRemove(member.personId)}>Remove</button>}
              {canWrite && confirmRemove === member.personId && (
                <span className="cluster">
                  <button className="btn small danger" type="button" onClick={() => void removeMember(member.personId)}>Confirm</button>
                  <button className="btn small ghost" type="button" onClick={() => setConfirmRemove(null)}>Keep</button>
                </span>
              )}
            </div>
          ))}
          {canWrite && (
            <form className="form-grid" style={{ marginTop: 12 }} onSubmit={(event) => void addMember(event)}>
              <div className="field"><label htmlFor="member-find">Find a person</label><input id="member-find" value={memberQuery} onChange={(event) => setMemberQuery(event.target.value)} placeholder="Name or instrument" /></div>
              <div className="field">
                <label htmlFor="member-person">Person</label>
                <select id="member-person" value={memberId} onChange={(event) => setMemberId(event.target.value)}>
                  <option value="">Choose</option>
                  {roster.map((person) => <option key={person.id} value={person.id}>{person.name}</option>)}
                </select>
              </div>
              <div className="field"><label htmlFor="member-role">Role in this group</label><input id="member-role" value={memberRole} onChange={(event) => setMemberRole(event.target.value)} maxLength={40} /></div>
              <button className="btn" type="submit">Add or update role</button>
              {people.length >= 100 && <p className="faint">The picker lists the first 100 people.</p>}
            </form>
          )}
        </article>
        <article className="panel panel-pad stack">
          <h2>Meetings</h2>
          {group.meetings.length === 0 && <p className="muted">No meetings yet.</p>}
          {group.meetings.map((item) => (
            <button key={item.id} className={item.id === meeting?.id ? "btn teal" : "btn ghost"} type="button" onClick={() => setMeetingId(item.id)}>
              {safeWhen(item.startsAt)} · {item.present} present
            </button>
          ))}
          {canWrite && (
            <form className="form-grid" onSubmit={(event) => void addMeeting(event)}>
              <div className="field"><label htmlFor="meet-when">Starts (Auckland)</label><input id="meet-when" type="datetime-local" value={when} onChange={(event) => setWhen(event.target.value)} required /></div>
              <div className="field"><label htmlFor="meet-place">Place</label><input id="meet-place" value={place} onChange={(event) => setPlace(event.target.value)} maxLength={140} /></div>
              <div className="field"><label htmlFor="meet-notes">Notes</label><textarea id="meet-notes" className="mbc-short" value={meetingNotes} onChange={(event) => setMeetingNotes(event.target.value)} maxLength={2000} /></div>
              <button className="btn" type="submit">Add meeting</button>
            </form>
          )}
          {meeting && (
            <div className="stack">
              <h3>{safeWhen(meeting.startsAt)}</h3>
              <p className="muted">{meeting.place || "Place not set"}{meeting.notes ? ` · ${meeting.notes}` : ""}</p>
              <p className="notice">Server headcount: {meeting.present}. The API does not return each name, so ticks are what this browser last saved.</p>
              {group.members.map((member) => (
                <label className="mbc-attend" key={member.personId}>
                  <input type="checkbox" checked={marks[member.personId] === true} disabled={!canWrite || pendingId === member.personId} onChange={(event) => void mark(member.personId, event.target.checked)} />
                  <span>{member.name}</span>
                  <Badge tone={marks[member.personId] === true ? "teal" : "mute"}>{attendanceWord(marks[member.personId])}</Badge>
                </label>
              ))}
              {!canWrite && <p className="faint">Marking attendance needs a coordinator login.</p>}
            </div>
          )}
        </article>
      </div>
    </section>
  );
}

function leaderOptions(people: Person[], group: GroupDetail): Person[] {
  if (!group.leaderPersonId || people.some((person) => person.id === group.leaderPersonId)) return people;
  return [{ id: group.leaderPersonId, name: group.leaderName ?? "Current leader", firstName: "", lastName: "", email: null, phone: null, status: "active", householdId: null, householdName: null, tags: [], instruments: [], roles: [], notes: "", groups: [] }, ...people];
}
